import { InterlistedExchange } from './interlisted-exchange.domain';
import { QuoteIssueType } from './quote-issue-type.domain';
import { QuoteTimeSeries } from './quote-time-series.domain';
import { VolumePace } from './volume-pace.domain';
import { RatingsConsensus, AnalystRatings } from './analyst-rating.domain';
import { Currency, Dividend, MarketType } from './market.domain';
import { SecuritySymbol, SymbolType } from './symbol.domain';

export enum QuoteDetailType {
  FUNDAMENTALS = 'FUNDAMENTALS',
  THOMSON = 'THOMSON',
  MOD = 'MOD'
}

/**
 * BaseQuote contains fields common to Quote and DetailedQuote interfaces.
 * Some renaming has to be done before this class can be used as a parent class,
 * e.g.: Quote.ask -> Quote.askPrice to align it with DetailedQuote naming.
 */
export class BaseQuote {
  askPrice?: number; // ask
  askSize?: number;  // askLots
  bidPrice?: number; // bid
  bidSize?: number;  // askLots
  dividend?: number;
  halted?: boolean; // securityHalted, halted, securityHalted : converted to SymbolStatus.HALTED
  lastPrice?: number;
  openInterest?: number;
  percentageChange?: number;
  distributionYield?: number;
  securityType?: string; // tradeableSecurityType?: SymbolType;
  symbol?: SecuritySymbol;
  timestamp?: Date;
  volume?: number;
}

export interface HoldingsQuote {
  description: string;
  lastPrice: number;
  priceChange: number;
  ask: number;
  askSize: number;
  bid: number;
  bidSize: number;
  volume: number;
  highPrice: number;
  yearHigh: number;
  lowPrice: number;
  yearLow: number;
}

// TODO: consolidate with the DetailedQuote
export interface Quote {
  symbol: SecuritySymbol;
  ask?: number; // stocks and options
  askSize?: number; // stocks and options
  bid?: number; // stocks and options
  bidSize?: number; // stocks and options
  currency?: Currency;
  desc?: string; // TODO - backend: Change this to description
  companyName?: string; // TODO - Another backend discrepancy. Used in top movers
  description?: string;
  dividend?: number;
  earningsPerShare?: number;
  highPrice?: number;
  lastPrice?: number;
  latestPercentChange?: number;
  latestPriceChange?: number;
  distributionYield?: number;
  previousDivAmount?: number;
  dividendPayDate?: Date;
  loadType?: string;
  lotSize?: number;
  lowPrice?: number;
  openInterest?: number;
  openPrice?: number;
  percentChange?: number;
  priceChange?: number;
  priceToEarnings?: number;
  // TODO - backend: Add quoteError and quoteValid to Quote
  expired?: boolean; // option
  securityHalted?: boolean;
  quoteError?: boolean;
  quoteValid?: boolean;
  rrspEligibility?: boolean;
  securityType?: string; // TODO: Point to SymbolType
  status?: QuoteStatus; // TODO: get rid of (halted,  quoteError,  expired) boolean flags and add here
  tick?: string;
  exchangeAgreementDeclined?: boolean;
  symbolError?: boolean;
  timestamp?: Date;
  volume?: number;
  xDividend?: Date;
  yearHigh?: number;
  yearLow?: number;
  yield?: number;
  marketClosed?: boolean;
  purchasePriceChange?: number;
  purchasePriceChangePercentage?: number;
  logoLight?: string;
}

export interface OptionsQuote extends Quote {
  daysToExpiry: number;
  shortDatedInd: boolean;
}

export interface MutualFundsQuote extends Quote {
  mutualFundLoadType: 'F' | 'R' | 'N';
}

export enum QuoteStatus {
  QUOTE_VALID = 'QUOTE_VALID',
  QUOTE_UNAVALIABLE = 'QUOTE_UNAVALIABLE',
  SYMBOL_HALTED = 'SYMBOL_HALTED',
  SYMBOL_NOT_FOUND = 'SYMBOL_NOT_FOUND',
  SYMBOL_INVALID = 'SYMBOL_INVALID',
  QUOTE_DENIED = 'QUOTE_DENIED',
  AGREEMENT_DECLINED = 'AGREEMENT_DECLINED',
  QUOTE_TIMEOUT = 'QUOTE_TIMEOUT',
  QUOTE_PROBLEM = 'QUOTE_PROBLEM'
}

export interface DetailedQuote {
  askLots?: number;
  askPrice?: number;
  assetsUnderManagement?: number;
  bidLots?: number;
  bidPrice?: number;
  country?: MarketType;
  daysToExpiry?: number;
  defaultTimeSeries?: QuoteTimeSeries;
  delayed?: boolean;
  distributionYield?: number;
  dividend?: Dividend;
  dollarChange?: number;
  fundBenchmark?: number;
  fundCategory?: string;
  expirationDate?: Date;
  estimatedEarnings?: number;
  grossManagementExpenseRatio?: number;
  delta?: number;
  gamma?: number;
  vega?: number;
  theta?: number;
  rho?: number;
  impliedVolatility?: number;
  indexValues?: number;
  inceptionDate?: Date;
  interlistedExchanges?: Array<InterlistedExchange>;
  securityHalted?: boolean;
  isHalted?: boolean;
  isRRSPEligible?: boolean;
  isPennyStock?: boolean;
  issueId?: string;
  issueType?: QuoteIssueType;
  lastPrice?: number;
  loadType?: string;
  market?: string;
  marketCap?: number;
  morningStarRating?: number;
  name?: string;
  nav?: number;
  netManagementExpenseRatio?: number;
  openInterest?: number;
  optionExpiryDate?: Date;
  previousClose?: number;
  strikePrice?: number;
  percentageChange?: number;
  priceHigh?: number;
  priceHigh1M?: number;
  priceHigh52W?: number;
  priceLow1M?: number;
  priceLow52W?: number;
  priceLow?: number;
  priceOpen?: number;
  peRatio?: number;
  consensus?: RatingsConsensus | AnalystRatings;
  sessionIsActive?: boolean;
  sessionIsAfterOpen?: boolean;
  sessionIsBeforeOpen?: boolean;
  symbol?: string;
  timestamp?: Date;
  type?: string;
  volume?: number;
  volumePace?: VolumePace;
  volumeTenDays?: number;
  tickerSymbolForTimeSeries?: string;
  timeSeriesMarketTime?: { open: number, close: number };
  timestampEasternTime?: string;
  tradeableSecurityType?: SymbolType;
  underlyingSymbol?: string;
  underlyingIssueType?: string;
  ytdPct?: number;
  warningMessage?: string;
  underlyingQuote?: DetailedQuote;
}
